import { createCache, StyleProvider } from "@ant-design/cssinjs";
import { DatePicker } from "antd";
import locale from "antd/locale/fr_FR";
import classnames from "classnames";
import dayjs from "dayjs";
import { get, isNil } from "lodash";
import { useEffect, useRef, useState } from "react";
import { MdOutlineKeyboard } from "react-icons/md";
import Input from "shared/components/Input";
import { resolveInputProps } from "utils/validation-rules";
import { applyFieldError } from "./fieldErrors";
import {
  DATE_EDITING_ATTRIBUTE,
  DATE_FIELD_ATTRIBUTE,
  DATE_NATIVE_ATTRIBUTE,
  DATE_VALUE_EVENT,
} from "./utils";

const ISO_DATE = "YYYY-MM-DD";
const FRAME_CLASS = "krs-date-frame";
const HINT_CLASS = "krs-date-hint";
const POPUP_GAP = 4;
const SCREEN_PICKER_ATTRIBUTE = "data-datepicker";

const isDateSlot = (slot) =>
  get(slot, "type") === "input" &&
  get(slot, "inputKind") === "date" &&
  get(slot, "binding.kind") === "entity_field";

const anchorAfter = (input) => {
  const next = input.nextElementSibling;
  if (!isNil(next) && next.hasAttribute(DATE_FIELD_ATTRIBUTE)) return next;
  const anchor = input.ownerDocument.createElement("span");
  anchor.setAttribute(DATE_FIELD_ATTRIBUTE, "");
  input.after(anchor);
  return anchor;
};

export const mountDateFields = (doc, slots = []) => {
  if (doc.documentElement.getAttribute(SCREEN_PICKER_ATTRIBUTE) === "on")
    return null;
  const items = [];
  for (const slot of slots.filter(isDateSlot)) {
    const inputs = doc.querySelectorAll(`input[data-kayros-slot='${slot.id}']`);
    for (const input of inputs) {
      input.setAttribute(DATE_NATIVE_ATTRIBUTE, "");
      items.push({ slot, input, anchor: anchorAfter(input) });
    }
  }
  return items.length ? { cache: createCache(), items } : null;
};

const openingDate = ({ min, max }) => {
  const today = dayjs().format(ISO_DATE);
  if (max && today > max) return dayjs(max);
  if (min && today < min) return dayjs(min);
  return dayjs();
};

const popupAlignTo = (frame) => {
  const picker = frame.querySelector(".ant-picker");
  if (isNil(picker)) return undefined;
  const frameBox = frame.getBoundingClientRect();
  const pickerBox = picker.getBoundingClientRect();
  return {
    offset: [
      frameBox.left - pickerBox.left,
      frameBox.bottom - pickerBox.bottom + POPUP_GAP,
    ],
  };
};

const ScreenLook = ({ processing, input, picker, disabled }) => {
  const shownError = useRef(false);
  const frameRef = useRef(null);
  const [popupAlign, setPopupAlign] = useState();

  useEffect(() => {
    if (processing.inputError) {
      shownError.current = true;
      applyFieldError(input, processing.inputError);
      return;
    }
    if (!shownError.current) return;
    shownError.current = false;
    applyFieldError(input, "");
  }, [input, processing.inputError]);

  const { disabledDate, value, onOpenChange } = processing.field;

  useEffect(() => () => input.removeAttribute(DATE_EDITING_ATTRIBUTE), [input]);

  const handleOpenChange = (open) => {
    if (open) {
      input.setAttribute(DATE_EDITING_ATTRIBUTE, "");
      setPopupAlign(popupAlignTo(frameRef.current));
    } else {
      input.removeAttribute(DATE_EDITING_ATTRIBUTE);
    }
    onOpenChange(open);
  };

  return (
    <div ref={frameRef} className={classnames(input.className, FRAME_CLASS)}>
      {processing.showHint && (
        <div className={HINT_CLASS} role="tooltip">
          <MdOutlineKeyboard size={18} />
          <span>{processing.texts.HINT}</span>
        </div>
      )}
      <DatePicker
        locale={locale}
        placeholder={processing.texts.PLACEHOLDER}
        suffixIcon={null}
        bordered={false}
        picker={picker}
        disabled={disabled}
        showToday={isNil(disabledDate) || !disabledDate(dayjs())}
        defaultPickerValue={isNil(value) ? openingDate(input) : undefined}
        getPopupContainer={() => input.ownerDocument.body}
        dropdownAlign={popupAlign}
        {...processing.field}
        onOpenChange={handleOpenChange}
      />
    </div>
  );
};

export const DateField = ({ input, cache, fieldDef, dateDeltas }) => {
  const doc = input.ownerDocument;
  const [value, setValue] = useState(input.value);

  useEffect(() => {
    const sync = () => setValue(input.value);
    input.addEventListener(DATE_VALUE_EVENT, sync);
    return () => input.removeEventListener(DATE_VALUE_EVENT, sync);
  }, [input]);

  const emit = (type) =>
    input.dispatchEvent(new doc.defaultView.Event(type, { bubbles: true }));

  const handleChange = (date) => {
    const next = date ? date.format(ISO_DATE) : "";
    input.value = next;
    setValue(next);
    emit("input");
  };

  return (
    <StyleProvider container={doc.head} cache={cache}>
      <Input
        type="date"
        variant="simple"
        look={ScreenLook}
        input={input}
        ownerDocument={doc}
        disabled={input.disabled}
        {...resolveInputProps(get(fieldDef, "inputConfig") || {}, dateDeltas)}
        value={value ? dayjs(value) : undefined}
        onChange={handleChange}
        onBlur={() => emit("focusout")}
      />
    </StyleProvider>
  );
};
